(ns hypnos.sleeplab.tile.table.consult-details
  "The table containing the patient's (prior) consults details."
  (:require ["aws-amplify" :as amp :refer (Auth)]
            [cljs.core.async :refer [go]]
            [cljs.core.async.interop :refer-macros [<p!]]
            [clojure.string :as str]
            [hypnos.config :as config]
            [hypnos.graphql.mutations :as mutations]
            [hypnos.i18n :refer [tr]]
            [hypnos.lookup :refer (lookup-manufacturer-by-id
                                   lookup-make-by-id
                                   lookup-model-by-id
                                   lookup-mask-by-model-id)]
            [hypnos.sleeplab.core.auth :as auth]
            [hypnos.sleeplab.subs]
            [hypnos.time :as t]
            [re-frame.core :as rf]
            [reagent.core :as reagent]))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Identities

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Helpers

(defn- set-token
  "Set the (authentication) token."
  [token]
  (go
    (let [session   (<p! (.currentSession Auth))
          jwt-token (.getJwtToken (.getAccessToken session))]
      (reset! token jwt-token))))

(defn- comp-by-recorded-at-desc
  [c1 c2]
  (let [dt1 (:consult/recorded-at c1)
        dt2 (:consult/recorded-at c2)]
    (t/before? dt2 dt1)))

(defn- therapist
  "Therapist working on the (patient's) consult."
  [consult]
  (str (get-in consult [:consult/user :user/first-name])
       " "
       (get-in consult [:consult/user :user/last-name])))

(defn- header
  "Patients table header (row)."
  []
  [:tr
   [:th]
   [:th (tr :ui.label/modified-on)]
   [:th (tr :ui.label/status)]
   [:th (tr :ui.label/service-type)]
   [:th (tr :ui.label/therapist)]
   [:th (tr :ui.label/selected-mask)]
   [:th]])

(defn- row
  "Table row containing the patient/consult details."
  [consult last-consult? token]
  (let [catalog        (rf/subscribe [:sleeplab/catalog])
        chevron-id     (str (:consult/id consult) "-chevron")
        details-row-id (str (:consult/id consult) "-details-row")
        ids            (lookup-mask-by-model-id @catalog (get-in consult [:consult/mask :model/id]))
        manufacturer   (lookup-manufacturer-by-id @catalog (:manufacturer/id ids))
        model          (lookup-model-by-id @catalog (:manufacturer/id ids) (:make/id ids) (:model/id ids))
        sized-ids      (lookup-mask-by-model-id @catalog (get-in consult [:consult/sized-mask :model/id]))
        sized-model    (lookup-model-by-id @catalog (:manufacturer/id sized-ids) (:make/id sized-ids) (:model/id sized-ids))]
    [:tr {:on-click (fn [_]
                      (let [down? (str/includes? (-> js/document
                                                     (.getElementById chevron-id)
                                                     (.-className))
                                                 "mdi-chevron-down")]
                        (-> js/document
                            (.getElementById details-row-id)
                            .-className
                            (set! (when down? "is-hidden")))
                        (-> js/document
                            (.getElementById chevron-id)
                            .-className
                            (set! (if down? "mdi mdi-chevron-up" "mdi mdi-chevron-down")))))}
     [:td [:span.icon (if last-consult?
                        [:i.mdi.mdi-chevron-down {:id chevron-id}]
                        [:i.mdi.mdi-chevron-up {:id chevron-id}])]]
     [:td (t/format-date t/MM-dd-YYYY-HH-MM-SS (t/to-default-time-zone (:consult/recorded-at consult)))]
     [:td (tr :ui/consult-status (:consult/status consult))]
     [:td (tr (keyword (str "ui.service-type/" (name (:consult/service-type consult)))))]
     [:td (therapist consult)]
     [:td (when (= :consult-state/completed (:consult/status consult))
            [:span
             (str (:manufacturer/name manufacturer) " " (:model/description model))
             (when sized-model
               [:span
                [:br]
                [:span.has-text-weight-semibold "Recommended Size: "]
                [:span.is-italic (:model/name sized-model)]]) ])]
     [:td
      (when (= :consult-state/completed (:consult/status consult))
        [:button.button.is-small.is-danger.is-light
         {:on-click (fn []
                      (let [link (str config/REST-URL
                                      "/report/mask-fit?consult-id=" (:consult/id consult)
                                      "&authtoken=" @token)]
                        (.open js/window link "_blank") ))}
         [:span.icon.is-small [:i.mdi.mdi-file-pdf]]])
      (when (= :consult-state/new (:consult/status consult))
        [:a.button.is-warning.is-small
         {:href     "/"
          :on-click (fn [_]
                      (let [consult-id (:consult/id consult)]
                        (rf/dispatch-sync [:amplify/mutation
                                           mutations/update-consult-status
                                           {:consult {:id (str consult-id)}
                                            :status  "canceled"}
                                           :sleeplab/refresh-patients
                                           :sleeplab/set-error])))}
         [:span (tr :ui.label/cancel)]])
      [:span " "]]]))

(defn- boolean-answer
  [pred positive-question?]
  (cond
    (true? pred)  [:span.tag {:class (if positive-question? "is-success" "is-danger")} (tr :ui.label/yes)]
    (false? pred) [:span.tag {:class (if positive-question? "is-danger" "is-success")} (tr :ui.label/no)]
    :else         [:span.tag.has-tooltip-arrow (tr :ui.label/na)]))

(defn- numeric-answer
  [v & [unit]]
  (if v
    (str v " " unit)
    "-"))

(defn- claustrophobia-level-answer
  [level]
  (if-let [class (case level
                0 "is-success"
                1 "is-danger"
                2 "is-danger"
                nil)]
    [:span.tag {:class class}
     [:span.has-tooltip-arrow
      {:data-tooltip (tr :ui/claustrophobia-level-long level)}
      (tr :ui/claustrophobia-level-short level)]]
    [:span.tag
     (tr :ui/claustrophobia-level-short level)]))

(defn- mouth-breathing-level-answer
  [level]
  (if-let [class (case level
                   0 "is-success"
                   1 "is-danger"
                   2 "is-danger"
                   3 "is-danger"
                   nil)]
    [:span.tag {:class class}
     [:span.has-tooltip-arrow
      {:data-tooltip (tr :ui/mouth-breathing-level-long level)}
      (tr :ui/mouth-breathing-level-short level)]]
    [:span.tag
     (tr :ui/mouth-breathing-level-short level)]))

(defn- details
  "Table row containing the patient/consult details."
  [auth catalog consult last-consult?]
  (let [manufacturer (lookup-manufacturer-by-id catalog
                                                (get-in consult [:consult/manufacturer :manufacturer/id]))
        make         (lookup-make-by-id catalog
                                        (:manufacturer/id manufacturer)
                                        (get-in consult [:consult/make :make/id]))
        model        (lookup-model-by-id catalog
                                         (:manufacturer/id manufacturer)
                                         (:make/id make)
                                         (get-in consult [:consult/model :model/id]))]
    [:tr {:id    (str (:consult/id consult) "-details-row")
          :class (when-not last-consult? "is-hidden")}
     [:td]
     [:td {:col-span 6}
      [:table.table.is-striped.is-fullwidth
       [:tbody
        [:tr
         [:th (tr :ui.label/ahi)]
         [:td {:col-span 3} [numeric-answer (:consult/ahi consult)]]
         [:th (tr :ui.label/weight)]
         [:td [numeric-answer (:consult/weight consult) (tr :ui.label/lbs)]]]
        [:tr
         [:th (tr :ui.label/modality)]
         [:td (if (:consult/modality consult)
                (tr (keyword (str "ui.modality/" (name (:consult/modality consult)))))
                "-")]
         [:th (case (:consult/modality consult)
                :modality/cpap       (tr :ui.label/cpap)
                :modality/bipap-vpap (tr :ui.label/ipap)
                nil)]
         [:td (case (:consult/modality consult)
                :modality/cpap       [numeric-answer (:consult/cpap consult) (tr :ui.label/cmH20)]
                :modality/bipap-vpap [numeric-answer (:consult/ipap consult) (tr :ui.label/cmH20)]
                nil)]
         [:th (when (= :modality/bipap-vpap (:consult/modality consult)) (tr :ui.label/epap))]
         [:td (when (= :modality/bipap-vpap (:consult/modality consult))
                [numeric-answer (:consult/epap consult) (tr :ui.label/cmH20)])]]
        [:tr
         [:th (tr :ui.label/prior-mask)]
         [:td {:col-span 3}
          (str/join " " [(:manufacturer/name manufacturer) (:make/name make) (:model/name model)])]
         [:th (tr :ui.label/comfortable?)]
         [:td [boolean-answer (:consult/mask-was-comfortable? consult) true]]]
        ;; details
        (if (not= :consult-state/new (:consult/status consult))
          [:tr                   ; first row
           [:th [:span.has-tooltip-arrow.has-tooltip-multiline
                 {:data-tooltip (tr :ui.survey.eye-dryness/question)}
                 (tr :ui.label/eye-dryness?)]]
           [:td [boolean-answer (:consult/eye-dryness? consult) false]]
           [:th [:span.has-tooltip-arrow.has-tooltip-multiline
                 {:data-tooltip (tr :ui.survey.dentures/question)}
                 (tr :ui.label/dentures?)]]
           [:td [boolean-answer (:consult/take-dentures-out? consult) false]]
           [:th [:span.has-tooltip-arrow.has-tooltip-multiline
                 {:data-tooltip (tr :ui.survey.chronic-issues/question)}
                 (tr :ui.label/chronic-issues?)]]
           [:td [boolean-answer (:consult/chronic-issues? consult) false]]]
          [:tr
           [:td {:col-span 6}
            [:div.notification.is-info.is-light
             (tr :ui.label/waiting-for-patient)]]])
        (when (not= :consult-state/new (:consult/status consult))
          [:tr                           ; second row
           [:th [:span.has-tooltip-arrow.has-tooltip-multiline
                 {:data-tooltip (tr :ui.survey.claustrophobia/question)}
                 (tr :ui.label/claustrophobia-level)]]
           [:td [claustrophobia-level-answer (:consult/claustrophobia-level consult)]]
           [:th [:span.has-tooltip-arrow.has-tooltip-multiline
                 {:data-tooltip (tr :ui.survey.mouth-breathing-level/question)}
                 (tr :ui.label/mouth-breathing-level)]]
           [:td [mouth-breathing-level-answer (:consult/mouth-breathing-level consult) false]]
           [:th [:span.has-tooltip-arrow.has-tooltip-multiline
                 {:data-tooltip (tr :ui.survey.deviated-septum/question)}
                 (tr :ui.label/deviated-septum?)]]
           [:td [boolean-answer (:consult/deviated-septum? consult) false]]])
        (when (not= :consult-state/new (:consult/status consult))
          [:tr                           ; third row
           [:th [:span.has-tooltip-arrow.has-tooltip-multiline
                 {:data-tooltip (tr :ui.survey.sleep-motion/question)}
                 (tr :ui.label/sleep-motion)]]
           [:td [boolean-answer (:consult/sleep-motion? consult) false]]
           [:th [:span.has-tooltip-arrow.has-tooltip-multiline
                 {:data-tooltip (tr :ui.survey.sleep-positions/question)}
                 (tr :ui.label/sleep-positions)]]
           [:td [:div.tags
                 (if (empty? (:consult/sleep-positions consult))
                   [:span.tag.has-tooltip-arrow (tr :ui.label/na)]
                   (for [sleep-position (:consult/sleep-positions consult)]
                     ^{:key (str (:consult/id consult) "-sleep-position-" (name sleep-position))}
                     [:span.tag.is-info
                      (tr :ui/sleep-position-short sleep-position)]))]]
           [:th [:span.has-tooltip-arrow.has-tooltip-multiline
                 {:data-tooltip (tr :ui.survey.pacemaker/question)}
                 (tr :ui.label/pacemaker?)]]
           [:td [boolean-answer (:consult/pacemaker? consult) false]]])
        ;; No issues row for SleepLab consult
        #_(when (not= :consult-state/new (:consult/status consult))
          [:tr                           ; fourth row
           [:th [:span.has-tooltip-arrow.has-tooltip-multiline
                 {:data-tooltip (tr :ui.survey.issues/question)}
                 (tr :ui.label/issues)]]
           [:td {:col-span 5}
            [:div.tags
             (for [issue (:consult/issues consult)]
               ^{:key (str (:consult/id consult) "-issue-" (name issue))}
               [:span.tag.is-danger
                [:span.has-tooltip-arrow.has-tooltip-multiline
                 {:data-tooltip (tr :ui/issue-long issue)}
                 (tr :ui/issue-short issue)]])]]])
        ;; recommendations
        (when (auth/authorized? auth :make-recommendation)
          (when-not (empty? (:consult/recommendations consult))
            (let [recs      (->> (:consult/recommendations consult)
                                 (sort-by (juxt :recommendation/confidence-max :recommendation/confidence-min)
                                          #(compare %2 %1))
                                 vec)
                  recs-rows (mapv (fn [rec]
                                    (let [ids          (lookup-mask-by-model-id catalog (get-in rec [:recommendation/model :model/id]))
                                          manufacturer (lookup-manufacturer-by-id catalog (:manufacturer/id ids))
                                          make         (lookup-make-by-id catalog (:manufacturer/id ids) (:make/id ids))
                                          model        (lookup-model-by-id catalog (:manufacturer/id ids) (:make/id ids) (:model/id ids))]
                                      ^{:key (str (:recommendation/id rec))}
                                      [:tr
                                       [:td (:manufacturer/name manufacturer)]
                                       [:td {:col-span 3} (:make/name make)]
                                       [:td (:model/name model)]
                                       [:td (:recommendation/confidence-min rec)]]))
                                  recs)
                  rows      (apply conj
                                   [^{:key "recommendation-seperator"}
                                    [:tr [:th {:col-span 6}]]
                                    ^{:key "recommendation-header"}
                                    [:tr
                                     [:th (tr :ui.label/manufacturer)]
                                     [:th {:col-span 3} (tr :ui.label/mask)]
                                     [:th (tr :ui.label/size)]
                                     [:th (tr :ui.label/fit-score)]]]
                                   recs-rows)
                  rows      (conj rows
                                  ^{:key "recommendation-note"}
                                  [:tr
                                   [:th (tr :ui.label/note)]
                                   [:td {:col-span 5} (:consult/recommendations-note consult)]])]
              (for [row rows] row))))]]]
     [:td]]))


;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; API

(defn table
  "The patient consult table."
  [patient]
  (let [token    (reagent/atom nil)
        catalog  (rf/subscribe [:sleeplab/catalog])
        auth     (rf/subscribe [:sleeplab/auth])
        consults (sort comp-by-recorded-at-desc (:consult/_patient patient))]
    (set-token token)
    (cond
      (empty? consults)
      [:div.card-content
       [:section.section
        [:div.content.has-text-grey.has-text-centered
         [:p [:span.icon.is-large [:i.mdi.mdi-handshake.mdi-48px]]]
         [:p (tr :ui.label/no-consults)]]]]
      :else
      [:div.table-container
       [:table.table.is-fullwidth.is-striped.is-hoverable
        [:thead
         [header]]
        [:tbody
         (doall
          (reverse
           (reduce (fn [acc [row details]] (conj acc row details))
                   '()
                   (for [consult consults
                         :let    [last-consult? (= (:consult/id consult) (get-in patient [:patient/last-consult :consult/id]))
                                  row            ^{:key (:consult/id consult)} [row consult last-consult? token]
                                  details        ^{:key (str (:consult/id consult) "-details")} [details @auth @catalog consult last-consult?]]]
                     [row details]))))]]])))
